/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Elements within OuiComboBox which would normally be tabbable (inputs, buttons) have been removed
 * from the tab order with tabindex={-1} so that we can control the keyboard navigation interface.
 */
/* eslint-disable jsx-a11y/role-has-required-aria-props */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { findPopoverPosition, htmlIdGenerator, keys } from '../../services';
import { OuiPortal } from '../portal';
import { OuiComboBoxOptionsList } from './combo_box_options_list';
import { getMatchingOptions, flattenOptionGroups, getSelectedOptionForSearchValue } from './matching_options';
import { OuiComboBoxInput } from './combo_box_input/combo_box_input';
import { getElementZIndex } from '../../services/popover';

/**
 * Because of how TypeScript's LibraryManagedAttributes is designed to handle defaultProps (https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-0.html#support-for-defaultprops-in-jsx)
 * we can't directly export the above Props definitions, as the defaulted values are not made optional
 * as it isn't processed by LibraryManagedAttributes. To get around this, we:
 * - remove the props which have default values applied
 *   - additionally re-define `options` and `selectedOptions` defaults, necessary as static members can't access generics and become never[]
 * - export (Props - Defaults) & Partial<Defaults>
 */

var initialSearchValue = '';
export class OuiComboBox extends Component {
  static defaultProps = {
    async: false,
    compressed: false,
    fullWidth: false,
    isClearable: true,
    options: [],
    selectedOptions: [],
    singleSelection: false,
    prepend: undefined,
    append: undefined,
    sortMatchesBy: 'none'
  };
  state = {
    activeOptionIndex: -1,
    hasFocus: false,
    isListOpen: false,
    listElement: null,
    listPosition: 'bottom',
    listZIndex: undefined,
    matchingOptions: getMatchingOptions(this.props.options, this.props.selectedOptions, initialSearchValue, this.props.async, Boolean(this.props.singleSelection), this.props.sortMatchesBy),
    searchValue: initialSearchValue,
    width: 0
  };
  _isMounted = false;
  rootId = htmlIdGenerator();

  // Refs
  comboBoxRefInstance = null;
  comboBoxRefCallback = ref => {
    // IE11 doesn't support the `relatedTarget` event property for blur events
    // but does add it for focusout. React doesn't support `onFocusOut` so here we are.
    if (this.comboBoxRefInstance) {
      this.comboBoxRefInstance.removeEventListener('focusout', this.onContainerBlur);
    }
    this.comboBoxRefInstance = ref;
    if (this.comboBoxRefInstance) {
      this.comboBoxRefInstance.addEventListener('focusout', this.onContainerBlur);
      const comboBoxBounds = this.comboBoxRefInstance.getBoundingClientRect();
      this.setState({
        width: comboBoxBounds.width
      });
    }
  };
  autoSizeInputRefInstance = null;
  autoSizeInputRefCallback = ref => {
    this.autoSizeInputRefInstance = ref;
  };
  searchInputRefInstance = null;
  searchInputRefCallback = ref => {
    this.searchInputRefInstance = ref;
    if (this.props.inputRef) this.props.inputRef(ref);
  };
  listRefInstance = null;
  listRefCallback = ref => {
    if (this.comboBoxRefInstance) {
      // find the zIndex of the combobox relative to the page body
      // and use that to depth-position the list box
      // adds an extra `100` to provide some defense around neighboring elements' positioning
      const listZIndex = getElementZIndex(this.comboBoxRefInstance, document.body) + 100;
      this.setState({
        listZIndex
      });
    }
    this.listRefInstance = ref;
  };
  toggleButtonRefInstance = null;
  toggleButtonRefCallback = ref => {
    this.toggleButtonRefInstance = ref;
  };
  optionsRefInstances = [];
  optionRefCallback = (index, ref) => {
    this.optionsRefInstances[index] = ref;
  };
  openList = () => {
    this.setState({
      isListOpen: true
    });
  };
  closeList = event => {
    if (event && event.target === this.searchInputRefInstance) {
      // really long search values / custom entries triggers a scroll event on the input
      // which the OuiComboBoxOptionsList passes through here
      return;
    }
    this.clearActiveOption();
    this.setState({
      listZIndex: undefined,
      isListOpen: false
    });
  };
  updatePosition = (listElement = this.state.listElement) => {
    if (!this._isMounted) {
      return;
    }
    if (!this.state.isListOpen) {
      return;
    }
    if (!listElement) {
      return;
    }

    // it's possible that updateListPosition is called when listElement is becoming visible, but isn't yet
    const listElementBounds = listElement.getBoundingClientRect();
    if (listElementBounds.width === 0 || listElementBounds.height === 0) {
      return;
    }
    if (!this.comboBoxRefInstance) {
      return;
    }
    const comboBoxBounds = this.comboBoxRefInstance.getBoundingClientRect();
    const {
      position,
      top
    } = findPopoverPosition({
      allowCrossAxis: false,
      anchor: this.comboBoxRefInstance,
      popover: listElement,
      position: 'bottom'
    });
    if (this.listRefInstance) {
      this.listRefInstance.style.top = `${top}px`;
      // listElement doesn't have its width set until after updating the position
      // which means the popover service won't know about the correct width
      // however, we already know where to position the element
      this.listRefInstance.style.left = `${comboBoxBounds.left + window.pageXOffset}px`;
      this.listRefInstance.style.width = `${comboBoxBounds.width}px`;
    }

    // Cache for future calls.
    this.setState({
      listElement,
      listPosition: position,
      width: comboBoxBounds.width
    });
  };
  incrementActiveOptionIndex = amount => {
    // If there are no options available, do nothing.
    if (!this.state.matchingOptions.length) {
      return;
    }
    this.setState(({
      activeOptionIndex,
      matchingOptions
    }) => {
      let nextActiveOptionIndex;
      if (activeOptionIndex < 0) {
        // If this is the beginning of the user's keyboard navigation of the menu, then we'll focus
        // either the first or last item.
        nextActiveOptionIndex = amount < 0 ? matchingOptions.length - 1 : 0;
      } else {
        nextActiveOptionIndex = activeOptionIndex + amount;
        if (nextActiveOptionIndex < 0) {
          nextActiveOptionIndex = matchingOptions.length - 1;
        } else if (nextActiveOptionIndex === matchingOptions.length) {
          nextActiveOptionIndex = 0;
        }
      }

      // Group titles are included in option list but are not selectable
      // Skip group title options
      const direction = amount > 0 ? 1 : -1;
      while (matchingOptions[nextActiveOptionIndex].isGroupLabelOption) {
        nextActiveOptionIndex = nextActiveOptionIndex + direction;
        if (nextActiveOptionIndex < 0) {
          nextActiveOptionIndex = matchingOptions.length - 1;
        } else if (nextActiveOptionIndex === matchingOptions.length) {
          nextActiveOptionIndex = 0;
        }
      }
      return {
        activeOptionIndex: nextActiveOptionIndex
      };
    });
  };
  hasActiveOption = () => {
    return this.state.activeOptionIndex > -1 && this.state.activeOptionIndex < this.state.matchingOptions.length;
  };
  clearActiveOption = () => {
    this.setState({
      activeOptionIndex: -1
    });
  };
  clearSearchValue = () => {
    this.onSearchChange('');
  };
  removeLastOption = () => {
    if (!this.props.selectedOptions.length) {
      return;
    }

    // Backspace will be used to delete the input, not a pill.
    if (this.state.searchValue.length) {
      return;
    }

    // Delete last pill.
    this.onRemoveOption(this.props.selectedOptions[this.props.selectedOptions.length - 1]);
    if (Boolean(this.props.singleSelection) && !this.state.isListOpen) {
      this.openList();
    }
  };
  addCustomOption = (isContainerBlur, searchValue) => {
    const {
      onCreateOption,
      options,
      selectedOptions,
      singleSelection,
      clearOnBlur
    } = this.props;
    const {
      matchingOptions
    } = this.state;
    const {
      hasFocus,
      isListOpen
    } = this.state;
    if (clearOnBlur && searchValue && (hasFocus === false || isListOpen === false)) {
      this.clearSearchValue();
      return;
    }
    if (this.doesSearchMatchOnlyOption()) {
      this.onAddOption(matchingOptions[0], isContainerBlur);
      return;
    }
    if (!onCreateOption) {
      return;
    }

    // Don't bother trying to create an option if the user hasn't typed anything.
    if (!searchValue) {
      return;
    }

    // Don't create the value if it's already been selected.
    if (getSelectedOptionForSearchValue(searchValue, selectedOptions)) {
      return;
    }

    // Add new custom pill if this is custom input, even if it partially matches an option.
    const isOptionCreated = onCreateOption(searchValue, flattenOptionGroups(options));

    // Expect the consumer to be explicit in rejecting a custom option.
    if (isOptionCreated === false) {
      return;
    }
    this.clearSearchValue();
    if (Boolean(singleSelection)) {
      // Adding a custom option to a single select that does not appear in the list of options
      this.closeList();
    }
  };
  doesSearchMatchOnlyOption = () => {
    const {
      searchValue
    } = this.state;
    if (this.state.matchingOptions.length !== 1) {
      return false;
    }
    return this.state.matchingOptions[0].label.toLowerCase() === searchValue.toLowerCase();
  };
  areAllOptionsSelected = () => {
    const {
      options,
      selectedOptions,
      async
    } = this.props;
    // Assume if this is async then there could be infinite options.
    if (async) {
      return false;
    }
    const flattenOptions = flattenOptionGroups(options).map(option => {
      return {
        ...option,
        label: option.label.trim().toLowerCase()
      };
    });
    let numberOfSelectedOptions = 0;
    selectedOptions.forEach(({
      label
    }) => {
      const trimmedLabel = label.trim().toLowerCase();
      if (flattenOptions.findIndex(option => option.label === trimmedLabel) !== -1) numberOfSelectedOptions += 1;
    });
    return flattenOptions.length === numberOfSelectedOptions;
  };
  onComboBoxFocus = event => {
    if (this.state.hasFocus) {
      return;
    }
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
    this.openList();
    this.setState({
      hasFocus: true
    });
  };
  setCustomOptions = isContainerBlur => {
    const {
      searchValue
    } = this.state;
    const {
      delimiter
    } = this.props;
    if (delimiter) {
      searchValue.split(delimiter).forEach(option => {
        if (option.length > 0) this.addCustomOption(isContainerBlur, option);
      });
    } else {
      this.addCustomOption(isContainerBlur, searchValue);
    }
  };
  onContainerBlur = event => {
    // close the options list, unless the use clicked on an option

    /**
     * FireFox returns `relatedTarget` as `null` for security reasons, but provides a proprietary `explicitOriginalTarget`.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Event/explicitOriginalTarget
     */
    const focusEvent = event;
    const relatedTarget = focusEvent.relatedTarget || focusEvent.explicitOriginalTarget;
    const focusedInOptionsList = relatedTarget && this.listRefInstance && this.listRefInstance.contains(relatedTarget);
    const focusedInInput = relatedTarget && this.comboBoxRefInstance && this.comboBoxRefInstance.contains(relatedTarget);
    if (!focusedInOptionsList && !focusedInInput) {
      this.closeList();
      if (this.props.onBlur) {
        this.props.onBlur(event);
      }
      this.setState({
        hasFocus: false
      });

      // If the user tabs away or changes focus to another element, take whatever input they've
      // typed and convert it into a pill, to prevent the combo box from looking like a text input.
      if (!this.hasActiveOption()) {
        this.setCustomOptions(true);
      }
    }
  };
  onKeyDown = event => {
    switch (event.key) {
      case keys.ARROW_UP:
        event.preventDefault();
        event.stopPropagation();
        if (this.state.isListOpen) {
          this.incrementActiveOptionIndex(-1);
        } else {
          this.openList();
        }
        break;
      case keys.ARROW_DOWN:
        event.preventDefault();
        event.stopPropagation();
        if (this.state.isListOpen) {
          this.incrementActiveOptionIndex(1);
        } else {
          this.openList();
        }
        break;
      case keys.BACKSPACE:
        event.stopPropagation();
        this.removeLastOption();
        break;
      case keys.ESCAPE:
        if (this.state.isListOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.closeList();
        }
        break;
      case keys.ENTER:
        event.preventDefault();
        event.stopPropagation();
        if (this.hasActiveOption()) {
          this.onAddOption(this.state.matchingOptions[this.state.activeOptionIndex]);
        } else {
          this.setCustomOptions(false);
        }
        break;
      case keys.TAB:
        // Disallow tabbing when the user is navigating the options.
        if (this.hasActiveOption() && this.state.isListOpen) {
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      default:
        if (this.props.onKeyDown) {
          this.props.onKeyDown(event);
        }
    }
  };
  onOptionEnterKey = option => {
    this.onAddOption(option);
  };
  onOptionClick = option => {
    this.onAddOption(option);
  };
  onAddOption = (addedOption, isContainerBlur) => {
    if (addedOption.disabled) {
      return;
    }
    const {
      onChange,
      selectedOptions,
      singleSelection: singleSelectionProp
    } = this.props;
    const singleSelection = Boolean(singleSelectionProp);
    const changeOptions = singleSelection ? [addedOption] : selectedOptions.concat(addedOption);
    if (onChange) {
      onChange(changeOptions);
    }
    this.clearSearchValue();
    this.clearActiveOption();
    if (!isContainerBlur) {
      if (this.searchInputRefInstance) {
        this.searchInputRefInstance.focus();
      }
    }
    if (singleSelection) {
      requestAnimationFrame(() => this.closeList());
    } else {
      this.setState({
        activeOptionIndex: this.state.matchingOptions.indexOf(addedOption)
      });
    }
  };
  onRemoveOption = removedOption => {
    const {
      onChange,
      selectedOptions
    } = this.props;
    if (onChange) {
      onChange(selectedOptions.filter(option => option !== removedOption));
    }
    this.clearActiveOption();
  };
  clearSelectedOptions = () => {
    const {
      onChange
    } = this.props;
    if (onChange) {
      onChange([]);
    }

    // Clicking the clear button will also cause it to disappear. This would result in focus
    // shifting unexpectedly to the body element so we set it to the input which is more reasonable,
    if (this.searchInputRefInstance) {
      this.searchInputRefInstance.focus();
    }
    if (!this.state.isListOpen) {
      this.openList();
    }
  };
  onComboBoxClick = () => {
    // When the user clicks anywhere on the box, enter the interaction state.
    if (this.searchInputRefInstance) {
      this.searchInputRefInstance.focus();
    }

    // If the user does this from a state in which an option has focus, then we need to reset it or clear it.
    if (Boolean(this.props.singleSelection) && this.props.selectedOptions.length === 1) {
      const selectedOptionIndex = this.state.matchingOptions.findIndex(option => option.label === this.props.selectedOptions[0].label && option.key === this.props.selectedOptions[0].key);
      this.setState({
        activeOptionIndex: selectedOptionIndex
      });
    } else {
      this.clearActiveOption();
    }
  };
  onOpenListClick = () => {
    if (this.searchInputRefInstance) {
      this.searchInputRefInstance.focus();
    }
    if (!this.state.isListOpen) {
      this.openList();
    }
  };
  onOptionListScroll = () => {
    if (this.searchInputRefInstance) {
      this.searchInputRefInstance.focus();
    }
  };
  onCloseListClick = () => {
    this.closeList();
  };
  onSearchChange = searchValue => {
    const {
      onSearchChange,
      delimiter
    } = this.props;
    if (onSearchChange) {
      const hasMatchingOptions = this.state.matchingOptions.length > 0;
      onSearchChange(searchValue, hasMatchingOptions);
    }
    this.setState({
      searchValue
    }, () => {
      if (searchValue && this.state.isListOpen === false) this.openList();
    });
    if (delimiter && searchValue.endsWith(delimiter)) {
      this.setCustomOptions(false);
    }
  };
  componentDidMount() {
    this._isMounted = true;

    // TODO: This will need to be called once the actual stylesheet loads.
    setTimeout(() => {
      if (this.autoSizeInputRefInstance) {
        this.autoSizeInputRefInstance.copyInputStyles();
      }
    }, 100);
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      options,
      selectedOptions,
      singleSelection,
      sortMatchesBy
    } = nextProps;
    const {
      activeOptionIndex,
      searchValue
    } = prevState;

    // Calculate and cache the options which match the searchValue, because we use this information
    // in multiple places and it would be expensive to calculate repeatedly.
    const matchingOptions = getMatchingOptions(options, selectedOptions, searchValue, nextProps.async, Boolean(singleSelection), sortMatchesBy);
    const stateUpdate = {
      matchingOptions
    };
    if (activeOptionIndex >= matchingOptions.length) {
      stateUpdate.activeOptionIndex = -1;
    }
    return stateUpdate;
  }
  updateMatchingOptionsIfDifferent = newMatchingOptions => {
    const {
      matchingOptions,
      activeOptionIndex
    } = this.state;
    const {
      singleSelection,
      selectedOptions
    } = this.props;
    let areOptionsDifferent = false;
    if (matchingOptions.length !== newMatchingOptions.length) {
      areOptionsDifferent = true;
    } else {
      for (let i = 0; i < matchingOptions.length; i++) {
        if (matchingOptions[i].label !== newMatchingOptions[i].label) {
          areOptionsDifferent = true;
          break;
        }
      }
    }
    if (areOptionsDifferent) {
      this.optionsRefInstances = [];
      let nextActiveOptionIndex = activeOptionIndex;
      // ensure that the currently selected single option is active if it is in the matchingOptions
      if (Boolean(singleSelection) && selectedOptions.length === 1) {
        if (newMatchingOptions.includes(selectedOptions[0])) {
          nextActiveOptionIndex = newMatchingOptions.indexOf(selectedOptions[0]);
        }
      }
      this.setState({
        matchingOptions: newMatchingOptions,
        activeOptionIndex: nextActiveOptionIndex
      });
      if (!newMatchingOptions.length) {
        // Prevent endless setState -> componentWillUpdate -> setState loop.
        if (this.hasActiveOption()) {
          this.clearActiveOption();
        }
      }
    }
  };
  componentDidUpdate() {
    const {
      options,
      selectedOptions,
      singleSelection,
      sortMatchesBy
    } = this.props;
    const {
      searchValue
    } = this.state;

    // React 16.3 has a bug (fixed in 16.4) where getDerivedStateFromProps
    // isn't called after a state change, and we track `searchValue` in state
    // instead we need to react to a change in searchValue here
    this.updateMatchingOptionsIfDifferent(getMatchingOptions(options, selectedOptions, searchValue, this.props.async, Boolean(singleSelection), sortMatchesBy));
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  render() {
    const {
      'data-test-subj': dataTestSubj,
      async,
      className,
      compressed,
      customOptionText,
      fullWidth,
      icon,
      id,
      inputRef,
      isClearable,
      isDisabled,
      isInvalid,
      isLoading,
      noSuggestions,
      onBlur,
      onChange,
      onCreateOption,
      onFocus,
      onSearchChange,
      options,
      placeholder,
      renderOption,
      rowHeight,
      selectedOptions,
      singleSelection,
      prepend,
      sortMatchesBy,
      delimiter,
      append,
      autoFocus,
      ...rest
    } = this.props;
    const {
      activeOptionIndex,
      hasFocus,
      isListOpen,
      listPosition,
      searchValue,
      width,
      matchingOptions
    } = this.state;

    // Visually indicate the combobox is in an invalid state if it has lost focus but there is text entered in the input.
    // When custom options are disabled and the user leaves the combo box after entering text that does not match any
    // options, this tells the user that they've entered invalid input.
    const markAsInvalid = isInvalid || (hasFocus === false || isListOpen === false) && searchValue;
    const classes = classNames('ouiComboBox', className, {
      'ouiComboBox--compressed': compressed,
      'ouiComboBox--fullWidth': fullWidth,
      'ouiComboBox-isDisabled': isDisabled,
      'ouiComboBox-isInvalid': markAsInvalid,
      'ouiComboBox-isOpen': isListOpen
    });
    const value = selectedOptions.map(selectedOption => selectedOption.label).join(', ');
    let optionsList;
    if (!noSuggestions && isListOpen) {
      const optionsListDataTestSubj = dataTestSubj ? `${dataTestSubj}-optionsList` : undefined;
      optionsList = <OuiPortal>
          <OuiComboBoxOptionsList zIndex={this.state.listZIndex} activeOptionIndex={this.state.activeOptionIndex} areAllOptionsSelected={this.areAllOptionsSelected()} customOptionText={customOptionText} data-test-subj={optionsListDataTestSubj} fullWidth={fullWidth} isLoading={isLoading} listRef={this.listRefCallback} matchingOptions={matchingOptions} onCloseList={this.closeList} onCreateOption={onCreateOption} onOptionClick={this.onOptionClick} onOptionEnterKey={this.onOptionEnterKey} onScroll={this.onOptionListScroll} optionRef={this.optionRefCallback} options={options} position={listPosition} singleSelection={singleSelection} renderOption={renderOption} rootId={this.rootId} rowHeight={rowHeight} scrollToIndex={activeOptionIndex} searchValue={searchValue} selectedOptions={selectedOptions} updatePosition={this.updatePosition} width={width} delimiter={delimiter} getSelectedOptionForSearchValue={getSelectedOptionForSearchValue} />
        </OuiPortal>;
    }
    return (
      /**
       * Re: jsx-a11y/interactive-supports-focus
       * Focus is managed and is placed on the textbox element (`OuiComboBoxInput`)
       *
       * Re: jsx-a11y/role-has-required-aria-props
       * Expansion is managed and required `aria-controls` prop is placed on the textbox element (`OuiComboBoxInput`)
       *
       * Reference for both: https://www.w3.org/TR/2017/REC-wai-aria-1.1-20171214/#combobox,
       * which verifies that this implementation follows the spec.
       */
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus
      <div {...rest} aria-expanded={isListOpen} aria-haspopup="listbox" className={classes} data-test-subj={dataTestSubj} onKeyDown={this.onKeyDown} ref={this.comboBoxRefCallback} role="combobox">
        <OuiComboBoxInput autoSizeInputRef={this.autoSizeInputRefCallback} compressed={compressed} focusedOptionId={this.hasActiveOption() ? this.rootId(`_option-${this.state.activeOptionIndex}`) : undefined} fullWidth={fullWidth} hasSelectedOptions={selectedOptions.length > 0} icon={icon} id={id} inputRef={this.searchInputRefCallback} isDisabled={isDisabled} isListOpen={isListOpen} noIcon={!!noSuggestions} onChange={this.onSearchChange} onClear={isClearable && !isDisabled ? this.clearSelectedOptions : undefined} onClick={this.onComboBoxClick} onCloseListClick={this.onCloseListClick} onFocus={this.onComboBoxFocus} onOpenListClick={this.onOpenListClick} onRemoveOption={this.onRemoveOption} placeholder={placeholder} rootId={this.rootId} searchValue={searchValue} selectedOptions={selectedOptions} singleSelection={singleSelection} toggleButtonRef={this.toggleButtonRefCallback} updatePosition={this.updatePosition} value={value} append={singleSelection ? append : undefined} prepend={singleSelection ? prepend : undefined} isLoading={isLoading} autoFocus={autoFocus} />
        {optionsList}
      </div>
    );
  }
}

// @internal
OuiComboBox.propTypes = {
  "data-test-subj": PropTypes.string,
  /**
     * Updates the list of options asynchronously
     */
  async: PropTypes.bool.isRequired,
  className: PropTypes.string,
  /**
     * When `true` creates a shorter height input
     */
  compressed: PropTypes.bool.isRequired,
  /**
     * When `true` expands to the entire width available
     */
  fullWidth: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired, PropTypes.bool.isRequired]),
  id: PropTypes.string,
  inputRef: PropTypes.any,
  /**
     * Shows a button that quickly clears any input
     */
  isClearable: PropTypes.bool.isRequired,
  /**
     * Disables the input
     */
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  /**
     * Swaps the dropdown options for a loading spinner
     */
  isLoading: PropTypes.bool,
  /**
     * Doesn't show the suggestions list/dropdown
     */
  noSuggestions: PropTypes.bool,
  onBlur: PropTypes.any,
  /**
     * Called every time the query in the combo box is parsed
     */
  onChange: PropTypes.func,
  onFocus: PropTypes.any,
  onKeyDown: PropTypes.any,
  /**
     * Called every time the text query in the search box is parsed
     */
  onSearchChange: PropTypes.func,
  /**
     * Sets the placeholder of the input
     */
  placeholder: PropTypes.string,
  /**
     * Every option must be the same height and must be explicitly set if using a custom render
     */
  rowHeight: PropTypes.number,
  /**
     * When `true` only allows the user to select a single option. Set to `{ asPlainText: true }` to not render input selection as pills
     */
  singleSelection: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    asPlainText: PropTypes.bool
  }).isRequired]).isRequired,
  /**
     * Display matching options by:
     * `startsWith`: moves items that start with search value to top of the list;
     * `none`: don't change the sort order of initial object
     */
  sortMatchesBy: PropTypes.oneOf(["none", "startsWith"]).isRequired,
  /**
     * Creates an input group with element(s) coming before input. It won't show if `singleSelection` is set to `false`.
     * `string` | `ReactElement` or an array of these
     */
  prepend: PropTypes.any,
  /**
     * Creates an input group with element(s) coming after input. It won't show if `singleSelection` is set to `false`.
     * `string` | `ReactElement` or an array of these
     */
  append: PropTypes.any,
  /**
     * A special character to use as a value separator. Typically a comma `,`
     */
  delimiter: PropTypes.string,
  /**
     * Specifies that the input should have focus when the component loads
     */
  autoFocus: PropTypes.bool,
  /**
     * When `true` clears the input text when user focus out of the input box
     */
  clearOnBlur: PropTypes.bool,
  "aria-label": PropTypes.string
};
// @internal
export class OuiCompressedComboBox extends OuiComboBox {
  static defaultProps = {
    ...OuiComboBox.defaultProps,
    compressed: true
  };
}
try {
  OuiComboBox.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/combo_box/combo_box.tsx',
    description: '',
    displayName: 'OuiComboBox',
    methods: [],
    props: {
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      async: {
        defaultValue: {
          value: 'false'
        },
        description: 'Updates the list of options asynchronously',
        name: 'async',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: 'When `true` creates a shorter height input',
        name: 'compressed',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'When `true` expands to the entire width available',
        name: 'fullWidth',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean | IconType'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLInputElement) => void'
        }
      },
      isClearable: {
        defaultValue: {
          value: 'true'
        },
        description: 'Shows a button that quickly clears any input',
        name: 'isClearable',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: 'Disables the input',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Swaps the dropdown options for a loading spinner',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      noSuggestions: {
        defaultValue: null,
        description: "Doesn't show the suggestions list/dropdown",
        name: 'noSuggestions',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLDivElement>'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Called every time the query in the combo box is parsed',
        name: 'onChange',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(options: OuiComboBoxOptionOption<T>[]) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLDivElement>'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLDivElement>'
        }
      },
      onSearchChange: {
        defaultValue: null,
        description: 'Called every time the text query in the search box is parsed',
        name: 'onSearchChange',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(searchValue: string, hasMatchingOptions?: boolean) => void'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Sets the placeholder of the input',
        name: 'placeholder',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rowHeight: {
        defaultValue: null,
        description: 'Every option must be the same height and must be explicitly set if using a custom render',
        name: 'rowHeight',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      singleSelection: {
        defaultValue: {
          value: 'false'
        },
        description: 'When `true` only allows the user to select a single option. Set to `{ asPlainText: true }` to not render input selection as pills',
        name: 'singleSelection',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean | OuiComboBoxSingleSelectionShape'
        }
      },
      sortMatchesBy: {
        defaultValue: {
          value: "'none' as const"
        },
        description: 'Display matching options by:\n' + '`startsWith`: moves items that start with search value to top of the list;\n' + "`none`: don't change the sort order of initial object",
        name: 'sortMatchesBy',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"startsWith" | "none"',
          value: [{
            value: '"startsWith"'
          }, {
            value: '"none"'
          }]
        }
      },
      prepend: {
        defaultValue: {
          value: 'undefined'
        },
        description: "Creates an input group with element(s) coming before input. It won't show if `singleSelection` is set to `false`.\n" + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: {
          value: 'undefined'
        },
        description: "Creates an input group with element(s) coming after input. It won't show if `singleSelection` is set to `false`.\n" + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      delimiter: {
        defaultValue: null,
        description: 'A special character to use as a value separator. Typically a comma `,`',
        name: 'delimiter',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: 'Specifies that the input should have focus when the component loads',
        name: 'autoFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      clearOnBlur: {
        defaultValue: null,
        description: 'When `true` clears the input text when user focus out of the input box',
        name: 'clearOnBlur',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      options: {
        defaultValue: {
          value: '[]'
        },
        description: 'Array of OuiComboBoxOptionOption objects. See #OuiComboBoxOptionOption',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiComboBoxOptionOption<T>[]'
        }
      },
      customOptionText: {
        defaultValue: null,
        description: 'Creates a custom text option. You can use `{searchValue}` inside your string to better customize your text.\n' + "It won't show if there's no onCreateOption.",
        name: 'customOptionText',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onCreateOption: {
        defaultValue: null,
        description: '',
        name: 'onCreateOption',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(searchValue: string, options: OuiComboBoxOptionOption<T>[]) => boolean | void'
        }
      },
      renderOption: {
        defaultValue: null,
        description: '',
        name: 'renderOption',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(option: OuiComboBoxOptionOption<T>, searchValue: string, OPTION_CONTENT_CLASSNAME: string) => ReactNode'
        }
      },
      selectedOptions: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'selectedOptions',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiComboBoxOptionOption<T>[]'
        }
      }
    },
    extendedInterfaces: ['_OuiComboBoxProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}
try {
  OuiCompressedComboBox.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/combo_box/combo_box.tsx',
    description: '',
    displayName: 'OuiCompressedComboBox',
    methods: [],
    props: {
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      async: {
        defaultValue: null,
        description: 'Updates the list of options asynchronously',
        name: 'async',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      compressed: {
        defaultValue: {
          value: 'true'
        },
        description: 'When `true` creates a shorter height input',
        name: 'compressed',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: 'When `true` expands to the entire width available',
        name: 'fullWidth',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean | IconType'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLInputElement) => void'
        }
      },
      isClearable: {
        defaultValue: null,
        description: 'Shows a button that quickly clears any input',
        name: 'isClearable',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: 'Disables the input',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Swaps the dropdown options for a loading spinner',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      noSuggestions: {
        defaultValue: null,
        description: "Doesn't show the suggestions list/dropdown",
        name: 'noSuggestions',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLDivElement>'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Called every time the query in the combo box is parsed',
        name: 'onChange',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(options: OuiComboBoxOptionOption<T>[]) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLDivElement>'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLDivElement>'
        }
      },
      onSearchChange: {
        defaultValue: null,
        description: 'Called every time the text query in the search box is parsed',
        name: 'onSearchChange',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: '(searchValue: string, hasMatchingOptions?: boolean) => void'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Sets the placeholder of the input',
        name: 'placeholder',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rowHeight: {
        defaultValue: null,
        description: 'Every option must be the same height and must be explicitly set if using a custom render',
        name: 'rowHeight',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      singleSelection: {
        defaultValue: null,
        description: 'When `true` only allows the user to select a single option. Set to `{ asPlainText: true }` to not render input selection as pills',
        name: 'singleSelection',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: true,
        type: {
          name: 'boolean | OuiComboBoxSingleSelectionShape'
        }
      },
      sortMatchesBy: {
        defaultValue: null,
        description: 'Display matching options by:\n' + '`startsWith`: moves items that start with search value to top of the list;\n' + "`none`: don't change the sort order of initial object",
        name: 'sortMatchesBy',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: '"startsWith" | "none"',
          value: [{
            value: '"startsWith"'
          }, {
            value: '"none"'
          }]
        }
      },
      prepend: {
        defaultValue: null,
        description: "Creates an input group with element(s) coming before input. It won't show if `singleSelection` is set to `false`.\n" + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: null,
        description: "Creates an input group with element(s) coming after input. It won't show if `singleSelection` is set to `false`.\n" + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      delimiter: {
        defaultValue: null,
        description: 'A special character to use as a value separator. Typically a comma `,`',
        name: 'delimiter',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: 'Specifies that the input should have focus when the component loads',
        name: 'autoFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      clearOnBlur: {
        defaultValue: null,
        description: 'When `true` clears the input text when user focus out of the input box',
        name: 'clearOnBlur',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box.tsx',
          name: '_OuiComboBoxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      options: {
        defaultValue: null,
        description: 'Array of OuiComboBoxOptionOption objects. See #OuiComboBoxOptionOption',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'OuiComboBoxOptionOption<T>[]'
        }
      },
      customOptionText: {
        defaultValue: null,
        description: 'Creates a custom text option. You can use `{searchValue}` inside your string to better customize your text.\n' + "It won't show if there's no onCreateOption.",
        name: 'customOptionText',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onCreateOption: {
        defaultValue: null,
        description: '',
        name: 'onCreateOption',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(searchValue: string, options: OuiComboBoxOptionOption<T>[]) => boolean | void'
        }
      },
      renderOption: {
        defaultValue: null,
        description: '',
        name: 'renderOption',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(option: OuiComboBoxOptionOption<T>, searchValue: string, OPTION_CONTENT_CLASSNAME: string) => ReactNode'
        }
      },
      selectedOptions: {
        defaultValue: null,
        description: '',
        name: 'selectedOptions',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'OuiComboBoxOptionOption<T>[]'
        }
      }
    },
    extendedInterfaces: ['_OuiComboBoxProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}